<template>
    <div class="community-profile-container">
        <!-- Main Profile Card -->
        <div class="profile-card">
            <div class="profile-header">
                <!-- Profile Picture and Basic Info -->
                <div class="profile-info">
                    <ProfilePicture 
                        :mediaPath="communityProfile ? communityProfile.profile_picture : null"
                        :fullName="fullName"
                        size="large"
                        :altText="`Photo de profil de ${fullName}`"
                        :editable="true"
                        :profileId="this.$store.state.user.id"
                        @upload-success="handleProfilePictureUpload"
                        @upload-error="handleProfilePictureError"
                    />
                    <div class="profile-details">
                        <h2 class="profile-name">
                            {{ fullName }}
                        </h2>
                    </div>
                </div>

                <!-- Infos Section -->
                <div class="infos-section">
                    <div class="info-item">
                        <label>Niveaux</label>
                        <div class="tags">
                            <span 
                                v-for="level in this.$store.state.user.levels" 
                                :key="level"
                                class="tag tile level"
                            >
                                {{ getLevel(level) }}
                            </span>
                        </div>
                    </div>
                    <div class="info-item">
                        <label>Matières</label>
                        <span class="subjects">{{ formattedSubjects }}</span>
                    </div>
                    <!-- <div class="info-item">
                        <label>Expérience</label>
                        <span v-if="communityProfile?.experience_years" class="experience">{{communityProfile.experience_years}} ans</span>
                        <span v-else class="experience">Aucune expérience définie</span>
                    </div> -->
                </div>
            </div>

            <!-- Additional Details -->
            <div class="profile-details-grid">
                <div class="detail-item">
                    <label>Département</label>
                    <span>{{ this.$store.state.user.county_name }}</span>
                </div>
                <div class="detail-item">
                    <label>Ville</label>
                    <span>{{ this.$store.state.user.city }}</span>
                </div>
                <div class="detail-item">
                    <label>Établissement scolaire</label>
                    <span>{{ this.$store.state.user.school || 'Aucun' }}</span>
                </div>
                <div class="detail-item">
                    <label>Dernier diplôme obtenu</label>
                    <span>{{ this.$store.state.user.diploma || 'Non renseigné' }}</span>
                </div>
            </div>
        </div>

        <!-- Shared Files Section -->
        <div class="shared-files-section">
            <!-- Fil d'Ariane dossiers -->
         
            <SharedFilesTable 
                :sharedFiles="sharedFiles" 
                :totalCount="totalCount"
                :currentPage="currentPage"
                :totalPages="totalPages"
                :loading="sharedFilesLoading"
                :showSearch="true"
                :showFolders="true"
                :sharedFolders="sharedFolders"
                :canGoBack="folderStack.length > 0 || currentFolderId !== null"
                :breadcrumb="breadcrumb"
                @open-folder="handleOpenFolder"
                @go-back-folder="handleGoBackFolder"
                @go-root="goToRoot"
                @go-breadcrumb="goToBreadcrumb"
                @page-changed="handleSharedFilesPageChange"
            />
        </div>
        <!-- Create Profile Section (hidden when profile exists) -->
        <div v-if="!communityProfile" class="create-profile-section">
            <h2>Créer un profil de communauté</h2>
            <p>Vous n'avez pas encore de profil communautaire. Créez-en un pour commencer à partager avec d'autres professeurs.</p>
            <button 
                @click="createCommunityProfile" 
                :disabled="createLoading"
                class="create-button"
            >
                {{ createLoading ? 'Création en cours...' : 'Créer un profil' }}
            </button>
            <div v-if="createError" class="error-message">
                Erreur lors de la création: {{ createError }}
            </div>
            <div v-if="createSuccess" class="success-message">
                Profil créé avec succès ! Vérifiez la console pour voir les détails.
            </div>
        </div>
    </div>
</template>

<script>
import { createCommunityProfileService, getCommunityProfileService } from '../../../services/communityProfileService'
import SharedFilesTable from '../Resources/Resource/ShareSpace/SharedFilesTable.vue'
import ProfilePicture from './ProfilePicture.vue'
import { sharedFilesByIdService, sharedFoldersByIdService } from '../../../services/filesService'
import { getFolderService } from '../../../services/foldersService'

export default {
    name: 'CommunityProfile',
    components: {
        SharedFilesTable,
        ProfilePicture
    },
    data() {
        return {
            communityProfile: null,
            loading: false,
            error: null,
            createLoading: false,
            createError: null,
            createSuccess: false,
            sharedFiles: [],
            sharedFolders: [],
            totalCount: 0,
            currentPage: 0,
            totalPages: 0,
            sharedFilesLoading: false,
            itemsPerPage: 10,
            // navigation dossiers
            currentFolderId: null,
            folderStack: [],
            breadcrumb: []
        }
    },
    computed: {
        fullName() {
            if (this.communityProfile && this.communityProfile.full_name) {
                return this.communityProfile.full_name
            }
            return `${this.$store.state.user.last_name} ${this.$store.state.user.first_name}`
        },
        formattedSubjects() {
            if (this.$store.state.user.subjects && this.$store.state.user.subjects.length > 0) {
                return this.$store.state.user.subjects
                    .map(subject => this.getSubject(subject))
                    .join(', ')
            }
            return 'Aucune matière'
        }
    },
    methods: {
        getSubject(e) {
            if (this.$store.state.subjects) {
                return this.$store.state.subjects.find(x => x.id === e)?.name || e
            }
            else {
                return e
            }
        },
        getLevel(e) {
            if (this.$store.state.levels) {
                return this.$store.state.levels.find(x => x.id === e)?.name || e
            }
            else {
                return e
            }
        },
        async createCommunityProfile() {
            this.createLoading = true
            this.createError = null
            this.createSuccess = false
            
            try {
                const profileId = localStorage.profileId
                console.log('ProfileId utilisé:', profileId)
                const mockData = {
                    profile_id: profileId,
                    name: "Communauté Test",
                    description: "Une communauté de test",
                    type: "education",
                    is_public: true
                }
                
                const response = await createCommunityProfileService(mockData)
                console.log('Profil créé:', response)
                this.createSuccess = true
                
                // Recharger les données du profil
                this.loadCommunityProfile()
            } catch (error) {
                console.error('Erreur lors de la création:', error)
                this.createError = error.message || 'Erreur lors de la création du profil'
            } finally {
                this.createLoading = false
            }
        },
        
        async loadSharedFiles(page = 1) {
            try {
                this.sharedFilesLoading = true
                const response = await sharedFilesByIdService(this.$store.state.user.id, page, this.itemsPerPage, this.currentFolderId)
                
                if (response && response.results) {
                    this.sharedFiles = response.results
                    this.totalCount = response.count
                    this.totalPages = Math.ceil(response.count / this.itemsPerPage)
                } else {
                    this.sharedFiles = Array.isArray(response) ? response : []
                    this.totalCount = this.sharedFiles.length
                    this.totalPages = 1
                }
                // Charger les dossiers partagés en parallèle
                const folderResponse = await sharedFoldersByIdService(this.$store.state.user.id, page, this.itemsPerPage, this.currentFolderId)
                if (folderResponse && folderResponse.results) {
                    // Filtrer les dossiers racine si on est à la racine
                    if (!this.currentFolderId) {
                        this.sharedFolders = folderResponse.results.filter(f => f.parent_folder_id === null)
                    } else {
                        this.sharedFolders = folderResponse.results
                    }
                } else {
                    const raw = Array.isArray(folderResponse) ? folderResponse : []
                    this.sharedFolders = !this.currentFolderId ? raw.filter(f => f.parent_folder_id === null) : raw
                }
            } catch (error) {
                console.error("Erreur lors du chargement des fichiers partagés:", error)
                this.sharedFiles = []
                this.sharedFolders = []
                this.totalCount = 0
                this.totalPages = 0
            } finally {
                this.sharedFilesLoading = false
            }
        },
        handleOpenFolder(folder) {
            if (!folder || !folder.id) return
            // navigation par route (comme ClassDrive)
            this.$router.push({ name: 'CommunityProfileFolder', params: { folderId: folder.id } })
        },
        handleGoBackFolder() {
            // retour d'un cran dans le breadcrumb => prendre l'avant-dernier si dispo, sinon racine
            if (this.breadcrumb.length <= 1) {
                this.goToRoot()
            } else {
                const prev = this.breadcrumb[this.breadcrumb.length - 2]
                this.$router.push({ name: 'CommunityProfileFolder', params: { folderId: prev.id } })
            }
        },
        goToRoot() {
            this.$router.push({ name: 'CommunityProfile' })
        },
        goToBreadcrumb(index) {
            const target = this.breadcrumb[index]
            if (!target) return this.goToRoot()
            this.$router.push({ name: 'CommunityProfileFolder', params: { folderId: target.id } })
        },
        async buildBreadcrumbFromFolder(folderId) {
            this.breadcrumb = []
            if (!folderId) return
            // remonte la chaîne des parents comme ClassDoc
            const chain = []
            let currentId = folderId
            // boucle de sécurité (évite boucles infinies)
            for (let i = 0; i < 50 && currentId; i++) {
                const folder = await getFolderService(currentId)
                if (!folder) break
                chain.push({ id: folder.id, name: folder.name, parent_folder_id: folder.parent_folder_id })
                currentId = folder.parent_folder_id
            }
            chain.reverse()
            this.breadcrumb = chain
        },
        handleSharedFilesPageChange(page) {
            this.currentPage = page
            this.loadSharedFiles(page + 1)
        },
        
        async loadCommunityProfile() {
            try {
                this.loading = true
                this.communityProfile = await getCommunityProfileService()
            } catch (error) {
                this.error = error.message
                console.error('❌ CommunityProfile - Erreur lors de la récupération du profil de communauté:', error)
            } finally {
                this.loading = false
            }
        },
        handleProfilePictureUpload(data) {
            console.log('Photo de profil uploadée avec succès:', data);
            
            if (this.communityProfile) {
                this.communityProfile.profile_picture = data.result;
            }
            this.$store.state.toast = {
                status: 'success',
                text: 'Photo de profil mise à jour avec succès',
            };
        },
        handleProfilePictureError(error) {
            console.error('Erreur lors de l\'upload de la photo de profil:', error);
            
            this.$store.state.toast = {
                status: 'error',
                text: error.message || 'Erreur lors de la mise à jour de la photo de profil',
            };
        }
    },
    async mounted() {
        await this.loadCommunityProfile()
        // Synchronise avec la route (folderId) pour navigation animée
        const folderId = this.$route?.params?.folderId ? parseInt(this.$route.params.folderId) : null
        this.currentFolderId = isNaN(folderId) ? null : folderId
        await this.buildBreadcrumbFromFolder(this.currentFolderId)
        await this.loadSharedFiles()
    },
    watch: {
        '$route.params.folderId': {
            immediate: false,
            async handler(newVal) {
                const id = newVal ? parseInt(newVal) : null
                this.currentFolderId = isNaN(id) ? null : id
                await this.buildBreadcrumbFromFolder(this.currentFolderId)
                this.currentPage = 0
                await this.loadSharedFiles(1)
            }
        }
    }
}
</script>

<style scoped lang="scss">
@import '../../../assets/scss/_variable.scss';

.community-profile-container {
    max-width: 1200px;
    width: 100%;
    margin: 16px auto;
    padding: 10px;
}

/* Profile Card */
.profile-card {
    background-color: white;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
}

.profile-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 30px;
}

.profile-info {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-right: 20px;
}

.profile-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.profile-name {
    @include sh2;
    color: #2d3748;
    margin: 0;
}

.location {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #718096;
    @include b2;
}

.location-icon {
    font-size: 1rem;
}

/* Infos Section */
.infos-section {
    background-color: $light-light-blue;
    padding: 20px;
    border-radius: 12px;
    min-width: 250px;
}

.infos-section h3 {
    margin: 0 0 15px 0;
    @include sh2;
    color: #2d3748;
}

.info-item {
    margin-bottom: 12px;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-item label {
    display: block;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 4px;
    @include sh2;
}

.tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.tag {
    background-color: #e2e8f0;
    color: #4a5568;
    padding: 3px 10px;
    border-radius: 16px;
    @include s2;
    font-weight: 500;
}

.subjects {
    color: #3182ce;
    font-weight: 500;
    @include b2;
}

.experience {
    color: #2d3748;
    font-weight: 500;
    @include b2;
}

/* Profile Details Grid */
.profile-details-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.detail-item label {
    font-weight: 600;
    color: #4a5568;
    @include sh2;
}

.detail-item span {
    color: #2d3748;
    font-weight: 500;
    @include b2;
}

/* Shared Files Section */
.shared-files-section {
    margin-top: 25px;
}

/* Create Profile Section */
.create-profile-section {
    background-color: #f7fafc;
    padding: 25px;
    border-radius: 12px;
    text-align: center;
    margin-top: 25px;
}

.create-profile-section h2 {
    margin: 0 0 12px 0;
    color: #2d3748;
    @include sh2;
}

.create-profile-section p {
    color: #718096;
    margin-bottom: 20px;
    @include b2;
}

.create-button {
    background-color: #553c9a;
    color: white;
    border: none;
    padding: 10px 25px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    @include bt2;
    transition: background-color 0.2s;
}

.create-button:hover:not(:disabled) {
    background-color: #44337a;
}

.create-button:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
}

.error-message {
    color: #e53e3e;
    margin-top: 12px;
    padding: 10px;
    background-color: #fed7d7;
    border: 1px solid #feb2b2;
    border-radius: 8px;
    @include b2;
}

.success-message {
    color: #22543d;
    margin-top: 12px;
    padding: 10px;
    background-color: #c6f6d5;
    border: 1px solid #9ae6b4;
    border-radius: 8px;
    @include b2;
}

/* Responsive Design */
@media (max-width: 768px) {
    .profile-header {
        flex-direction: column;
        gap: 20px;
    }
    
    .profile-details-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .profile-details-grid {
        grid-template-columns: 1fr;
    }
    
    .profile-info {
        flex-direction: column;
        text-align: center;
    }
}
</style>

